import * as BotpressAPI from "./omniBotpressAPI.js";

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const extractText = message => {
  if (message == null) {
    return "";
  }
  if (typeof message.payload?.text === "string") {
    return message.payload.text;
  }
  if (typeof message.payload?.block?.text === "string") {
    return message.payload.block.text;
  }
  return "";
};

const byCreatedAtAsc = (a, b) => new Date(a.createdAt) - new Date(b.createdAt);

// The analysis bot receives these transcripts through the Chat API user `data`
// field, which has a small size cap. Full Botpress message objects (ids,
// timestamps, tags, metadata, nested payloads) blow past it, so the transcripts
// get dropped and the bot reports "some data is missing". Reduce each turn to
// just its direction and text, preserving the `payload.text` path the bot reads.
// The Chat API hands back messages newest-first, so sort before compacting —
// otherwise the bot is asked to compare two conversations read backwards.
const compactConversation = messages =>
  (Array.isArray(messages) ? [...messages] : [])
    .sort(byCreatedAtAsc)
    .map(message => ({
      direction: message.direction,
      payload: { text: extractText(message) },
    }))
    .filter(turn => turn.payload.text);

// Poll the conversation until the analysis bot has finished answering. Our own
// prompt is excluded both by user id and by the id the send returned, because a
// user record without an id would otherwise make our own message look like the
// bot's reply. The bot commonly answers in several messages (an acknowledgement
// first, the JSON after), so keep polling until no new message has arrived for
// `settleMs` rather than returning on the first one.
const waitForBotReplies = async ({
  botODM,
  key,
  conversationId,
  userId,
  sentMessageId,
  timeoutMs,
  pollMs,
  settleMs,
}) => {
  const deadline = Date.now() + timeoutMs;
  let replies = [];
  let lastChangeAt = null;

  while (Date.now() < deadline) {
    await sleep(pollMs);

    const data = await BotpressAPI.listConversationMessages({ botODM, key, conversationId });
    const all = Array.isArray(data?.messages) ? data.messages : [];
    const botMessages = all
      .filter(message => message.id !== sentMessageId)
      .filter(message => userId == null || message.userId !== userId)
      .sort(byCreatedAtAsc);

    if (botMessages.length > replies.length) {
      replies = botMessages;
      lastChangeAt = Date.now();
    } else if (replies.length > 0 && Date.now() - lastChangeAt >= settleMs) {
      return replies;
    }
  }

  return replies;
};

const parseResults = replyText => {
  if (!replyText) {
    return null;
  }
  try {
    return JSON.parse(replyText);
  } catch (error) {
    return extractJsonBlock(replyText);
  }
};

const extractJsonBlock = replyText => {
  const match = replyText.match(/\{[\s\S]*\}/);
  if (match) {
    try {
      return JSON.parse(match[0]);
    } catch (error) {
      return null;
    }
  }
  return null;
};

export const runConversationComparison = async ({
  analyticsBotODM,
  originalConversation,
  newConversation,
  onProgress = () => {},
  replyTimeoutMs = 90000,
  pollMs = 2500,
  settleMs = 5000,
}) => {
  if (analyticsBotODM == null) {
    throw new Error("runConversationComparison: analyticsBotODM is required");
  }

  onProgress({ phase: "creating-user" });
  const userResponse = await BotpressAPI.createUser({ botODM: analyticsBotODM });
  const key = userResponse?.key;
  const userId = userResponse?.user?.id;
  if (!key) {
    throw new Error("runConversationComparison: failed to create user (no key returned)");
  }

  onProgress({ phase: "sending-data" });
  // The bot reads these off the incoming message (event.payload) — the Chat API
  // user-data path is not readable by its "Get User Data" step, so we ride the
  // transcripts on the message itself. `singleConversation` is intentionally
  // omitted (this is a comparison, not a single-conversation analysis), and the
  // older `originalConversation`/`newConversation` names the bot never reads are
  // dropped. Values are compacted JSON strings to stay small.
  const comparisonPayload = {
    analysisType: "assessmentComparison",
    oldestConversation: JSON.stringify(compactConversation(originalConversation)),
    newestConversation: JSON.stringify(compactConversation(newConversation)),
  };

  onProgress({ phase: "creating-conversation" });
  const conversationResponse = await BotpressAPI.createConversation({
    botODM: analyticsBotODM,
    key,
  });
  const conversationId = conversationResponse?.conversation?.id;
  if (!conversationId) {
    throw new Error("runConversationComparison: failed to create conversation (no id returned)");
  }

  onProgress({ phase: "requesting-analysis" });
  const sendResponse = await BotpressAPI.sendMessage({
    botODM: analyticsBotODM,
    key,
    conversationId,
    extraPayload: comparisonPayload,
    text: "Compare the original and new conversations and return the assessment results as JSON with at least a 'summary' field.",
  });
  const sentMessageId = sendResponse?.message?.id;

  onProgress({ phase: "awaiting-analysis" });
  const replies = await waitForBotReplies({
    botODM: analyticsBotODM,
    key,
    conversationId,
    userId,
    sentMessageId,
    timeoutMs: replyTimeoutMs,
    pollMs,
    settleMs,
  });
  const replyText = replies.map(extractText).filter(Boolean).join("\n\n");

  // A reply that isn't JSON means the bot didn't run its comparison flow (e.g.
  // "some data is missing"), so hand back no results rather than saving that
  // text as if it were a summary.
  const parsed = parseResults(replyText);
  const results =
    parsed != null && typeof parsed === "object"
      ? {
          ...parsed,
          summary: parsed.summary || replyText,
          comparedAt: new Date().toISOString(),
        }
      : null;

  onProgress({ phase: "done", conversationId });
  return { conversationId, results, rawReply: replyText };
};

export default runConversationComparison;
